import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import {
  EMPTY_PROGRESS,
  rememberSearch,
  searchCatalog,
  type Channel,
  type Credentials,
  type Program,
  type SearchTab,
  type VodItem,
  type WatchProgress,
} from '@player/domain';
import { PhoneSearchScreen, SearchScreen } from '@player/ui';
import { usePhoneLayout } from './usePhoneLayout.js';
import type { Composition } from './composition.js';
import type { CatalogCache } from './catalogCache.js';

/**
 * Quel che serve alla ricerca, preso dall'archivio dell'apparecchio.
 *
 * ## Perché un contenitore e non un `useEffect` dentro `LiveHost`
 *
 * Perché il catalogo on demand è **grosso**: su un'installazione vera 10 633 film e 2 396 serie. Chi
 * apre l'app per guardare la televisione non deve pagarne il caricamento, ed è la stessa ragione per
 * cui `VodHost` si monta solo entrando in Film o Serie TV. Montando questo solo dentro Cerca, chi non
 * cerca non carica niente — e chi cerca lo legge da `localStorage`, senza chiamate, a meno che sia
 * scaduto per ore d'uso.
 *
 * ## Le ricerche recenti stanno qui e non nella schermata
 *
 * Perché devono **sopravvivere all'uscita**: si cerca, si guarda un film, si torna a cercare e
 * l'elenco dev'esserci ancora. Tenendole nello stato della schermata se ne andrebbero a ogni
 * smontaggio, cioè proprio quando servono.
 */

export interface SearchHostProps {
  readonly composition: Composition;
  readonly cache: CatalogCache;
  readonly credentials: Credentials;
  readonly channels: ReadonlyArray<Channel>;
  /** I programmi già scaricati dalla guida: la ricerca non ne chiede altri. */
  readonly programs: ReadonlyArray<Program>;
  readonly nowMs: number;
  readonly reminderIds: ReadonlyArray<string>;
  readonly onToggleReminder: (channelId: string, startMs: number) => void;
  readonly onOpenChannel: (channelId: string) => void;
  readonly onOpenVod: (item: VodItem) => void;
  readonly onExit: () => void;
}

export function SearchHost({
  composition,
  cache,
  credentials,
  channels,
  programs,
  nowMs,
  reminderIds,
  onToggleReminder,
  onOpenChannel,
  onOpenVod,
  onExit,
}: SearchHostProps): ReactNode {
  const phone = usePhoneLayout();
  const [vod, setVod] = useState<ReadonlyArray<VodItem>>([]);
  const [progress, setProgress] = useState<WatchProgress>(EMPTY_PROGRESS);
  const [recent, setRecent] = useState<ReadonlyArray<string>>([]);
  /*
    Query e scheda, per la versione telefono.

    Stanno qui e non nella schermata perché la tastiera di sistema si chiude a ogni rimontaggio: un
    campo che perde il fuoco mentre si scrive è il modo più rapido di rendere inutile una ricerca.
  */
  const [query, setQuery] = useState('');
  const [tab, setTab] = useState<SearchTab>('all');

  const results = useMemo(
    () => searchCatalog({ query, channels, programs, vod, nowMs }),
    [query, channels, programs, vod, nowMs],
  );

  useEffect(() => {
    let cancelled = false;
    void Promise.all([
      cache.vod(credentials, 'movie', false),
      cache.vod(credentials, 'series', false),
      cache.progress(),
      cache.recentSearches(),
    ])
      .then(([movies, series, stored, searches]) => {
        if (cancelled) return;
        setVod([...movies.items, ...series.items]);
        setProgress(stored);
        setRecent(searches);
      })
      /*
        Se il catalogo on demand non si legge, si cerca lo stesso fra canali e programmi.

        Non è un ripiego silenzioso: la ricerca non promette un insieme fisso di sezioni, e le sezioni
        senza risultati non si disegnano comunque. Restare con la schermata vuota perché i film non
        sono arrivati significherebbe non poter cercare **nemmeno un canale**, che è la cosa che qui
        funziona sempre perché il listino è già in memoria.
      */
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [cache, credentials]);

  const changeRecent = useCallback(
    (next: ReadonlyArray<string>) => {
      setRecent(next);
      void cache.saveRecentSearches(next);
    },
    [cache],
  );

  if (phone) {
    /*
      La ricerca sul telefono: il campo in cima, le schede sotto, e i risultati.

      Qui query e scheda stanno **in questo componente** e non nella schermata, al contrario della
      versione per il televisore che se li tiene dentro. Non è una differenza di gusto: la schermata
      del telefono riceve i risultati già fatti, perché su un telefono la tastiera di sistema copre
      metà schermo e il testo va ricalcolato a ogni battuta — tenerlo qui evita di rimontare la
      schermata, che chiuderebbe la tastiera a ogni lettera.

      I risultati li compone `searchCatalog`, **la stessa funzione del televisore**: due ricerche
      scritte due volte trovano cose diverse per lo stesso testo.
    */
    return (
      <PhoneSearchScreen
        query={query}
        onQueryChange={setQuery}
        results={results}
        activeTab={tab}
        onPickTab={setTab}
        recent={recent}
        onPickRecent={(quale) => setQuery(quale)}
        onOpen={(quale, id) => {
          /*
            Si ricorda la ricerca **quando si sceglie un risultato**, non a ogni lettera battuta:
            l'elenco dei recenti si riempirebbe di «t», «te», «tel». La regola di inserimento —
            niente doppioni, i più recenti per primi, quanti tenerne — sta in `rememberSearch`, la
            stessa che usa il televisore.
          */
          changeRecent(rememberSearch(recent, query));

          if (quale === 'channel') {
            onOpenChannel(id);
            return;
          }
          /*
            Il titolo intero, non il suo identificativo: chi apre una scheda vuole l'oggetto, e
            cercarlo di nuovo più in là significherebbe poterlo non trovare.
          */
          const titolo = vod.find((voce) => voce.id === id);
          if (titolo !== undefined) onOpenVod(titolo);
        }}
      />
    );
  }

  return (
    <SearchScreen
      focusPort={composition.focusPort}
      channels={channels}
      programs={programs}
      vod={vod}
      progress={progress}
      nowMs={nowMs}
      recent={recent}
      onRecentChange={changeRecent}
      onOpenChannel={onOpenChannel}
      onOpenVod={onOpenVod}
      onToggleReminder={onToggleReminder}
      reminderIds={reminderIds}
      onExit={onExit}
    />
  );
}
